import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Bell,
  Settings,
  Trophy,
  Eye,
  EyeOff,
  Clock,
  RefreshCw,
  Save,
  Loader2,
  MapPin,
  DollarSign,
  Users,
  Gift,
  Star
} from 'lucide-react';

// Prize type display config
const PRIZE_TYPES = {
  major: { label: 'Premio Mayor', icon: Trophy, color: 'text-amber-600', bg: 'bg-amber-50' },
  inverted: { label: 'Premio Invertido', icon: Star, color: 'text-purple-600', bg: 'bg-purple-50' },
  top_buyer: { label: 'Top Comprador', icon: Gift, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  triple: { label: 'Premio Triple', icon: Star, color: 'text-orange-600', bg: 'bg-orange-50' },
  referral: { label: 'Top Referidor', icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' }
};

export default function AdminSocialProof() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    enabled: true,
    interval_min: 25,
    interval_max: 40,
    max_per_session: 10,
    display_duration: 6
  });
  const [prizes, setPrizes] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsData, prizesData] = await Promise.all([
        api.admin.getSocialProofSettings(),
        api.admin.getPaidPrizes()
      ]);
      setSettings(settingsData);
      setPrizes(prizesData.prizes || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.admin.updateSocialProofSettings(settings);
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async () => {
    const newEnabled = !settings.enabled;
    setSettings(prev => ({ ...prev, enabled: newEnabled }));
    
    try {
      await api.admin.updateSocialProofSettings({ enabled: newEnabled });
      toast.success(newEnabled ? 'Globos activados' : 'Globos desactivados');
    } catch (error) {
      setSettings(prev => ({ ...prev, enabled: !newEnabled }));
      toast.error('Error al cambiar estado');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="page-container pt-6 space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-32 w-full rounded-2xl" />
        <Skeleton className="h-48 w-full rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="page-container pt-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin">
            <Button variant="ghost" size="icon" className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <h1 className="heading-2">Prueba Social</h1>
        </div>
        <Button variant="outline" size="icon" onClick={fetchData}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Enable/Disable Toggle */}
      <Card className={`card-base ${settings.enabled ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50'}`}>
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${settings.enabled ? 'bg-emerald-500' : 'bg-slate-400'}`}>
                {settings.enabled ? <Eye className="w-6 h-6 text-white" /> : <EyeOff className="w-6 h-6 text-white" />}
              </div>
              <div>
                <h3 className="font-semibold text-lg">
                  {settings.enabled ? 'Globos Activos' : 'Globos Desactivados'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {settings.enabled 
                    ? 'Los usuarios ven notificaciones de premios pagados'
                    : 'Las notificaciones están ocultas'
                  }
                </p>
              </div>
            </div>
            <Button
              onClick={toggleEnabled}
              variant={settings.enabled ? 'destructive' : 'default'}
              className="min-w-[100px]"
            >
              {settings.enabled ? 'Desactivar' : 'Activar'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 text-center">
            <Trophy className="w-8 h-8 mx-auto mb-2 text-amber-600" />
            <p className="text-2xl font-bold text-amber-700">{prizes.length}</p>
            <p className="text-xs text-amber-600">Premios Pagados</p>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-4 text-center">
            <Bell className="w-8 h-8 mx-auto mb-2 text-emerald-600" />
            <p className="text-2xl font-bold text-emerald-700">{settings.max_per_session}</p>
            <p className="text-xs text-emerald-600">Máx por sesión</p>
          </CardContent>
        </Card>
      </div>

      {/* Settings */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Configuración de Aparición
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Interval */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="interval_min">Intervalo mínimo (segundos)</Label>
              <Input
                id="interval_min"
                type="number"
                min="10"
                max="120"
                value={settings.interval_min}
                onChange={(e) => setSettings(prev => ({ ...prev, interval_min: parseInt(e.target.value) || 25 }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="interval_max">Intervalo máximo (segundos)</Label>
              <Input
                id="interval_max"
                type="number"
                min="15"
                max="180"
                value={settings.interval_max}
                onChange={(e) => setSettings(prev => ({ ...prev, interval_max: parseInt(e.target.value) || 40 }))}
              />
            </div>
          </div>

          {/* Duration and Max */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="display_duration">Duración visible (segundos)</Label>
              <Input
                id="display_duration"
                type="number"
                min="3"
                max="15"
                value={settings.display_duration}
                onChange={(e) => setSettings(prev => ({ ...prev, display_duration: parseInt(e.target.value) || 6 }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="max_per_session">Máximo por sesión</Label>
              <Input
                id="max_per_session"
                type="number"
                min="1"
                max="50"
                value={settings.max_per_session}
                onChange={(e) => setSettings(prev => ({ ...prev, max_per_session: parseInt(e.target.value) || 10 }))}
              />
            </div>
          </div>

          {/* Info */}
          <div className="bg-blue-50 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">¿Cómo funciona?</p>
                <ul className="list-disc list-inside space-y-1 text-blue-700">
                  <li>Los globos aparecen cada {settings.interval_min}-{settings.interval_max} segundos</li>
                  <li>Permanecen visibles {settings.display_duration} segundos</li>
                  <li>Máximo {settings.max_per_session} globos por visita</li>
                  <li>Solo se muestran premios realmente pagados</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <Button onClick={handleSave} disabled={saving} className="w-full">
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Guardar Configuración
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Paid Prizes List */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="w-5 h-5" />
            Premios Pagados (Fuente de Datos)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {prizes.length === 0 ? (
            <div className="text-center py-8">
              <Trophy className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground mb-2">No hay premios pagados registrados</p>
              <p className="text-sm text-muted-foreground">
                Los globos no se mostrarán hasta que haya premios marcados como pagados
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {prizes.map((prize) => {
                const typeConfig = PRIZE_TYPES[prize.type] || PRIZE_TYPES.major;
                const TypeIcon = typeConfig.icon;
                
                return (
                  <div 
                    key={prize.id}
                    className={`p-4 rounded-xl ${typeConfig.bg} border`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full bg-white flex items-center justify-center`}>
                          <TypeIcon className={`w-5 h-5 ${typeConfig.color}`} />
                        </div>
                        <div>
                          <p className="font-semibold">{prize.winner_name}</p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <MapPin className="w-3 h-3" />
                            <span>{prize.city || 'Colombia'}</span>
                            <span>•</span>
                            <span>{prize.raffle_code}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${typeConfig.color}`}>
                          {formatCurrency(prize.amount)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(prize.paid_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* How to Add Prizes Info */}
      <Card className="card-base bg-amber-50 border-amber-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Trophy className="w-5 h-5 text-amber-600 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-semibold mb-1">¿Cómo agregar premios?</p>
              <p className="text-amber-700">
                Los premios se agregan automáticamente cuando marcas un ticket como "Pagado" 
                en la sección de <strong>Gestión de Tickets</strong>, o cuando procesas 
                el premio mensual de referidos.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
